import * as S from './styles';
import { useSync } from '@/hooks';
import { Modal } from '@/components';
import { useMemo, useRef } from 'react';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

const GlobalModalSync = () => {
  const lastSpeedBps = useRef(0);
  
  const { 
    isLoading, 
    hasError, 
    noUpdate, 
    isSuccess, 
    preparing, 
    isOpenedModal,
    downloadSpeedBps,
    fileDownload,
    downloadingFiles,
    downloadingFileName,
    fileDownloadProgress,
    progressPercent,
    isDownloadingFiles,
  } = useSync();

  const currentLoading = isLoading || preparing;

  const speedLabel = useMemo(() => {
    const value = downloadSpeedBps || lastSpeedBps.current;
    if (downloadSpeedBps !== 0) lastSpeedBps.current = downloadSpeedBps;
    const kbps = (value * 8) / 1000;
    if (kbps >= 1000) {
      const mbps = (kbps / 1000).toFixed(1).replace(/\.0$/, '');
      return `${mbps}Mbps`;
    }
    return `${Math.round(kbps)}kbps`;
  }, [downloadSpeedBps]);

  const errorOnSync = useMemo(() => {
    return hasError && !currentLoading;
  }, [hasError, currentLoading]);

  const syncMessage = useMemo(() => {
    if (errorOnSync) return 'Falha na sincronização dos dados, por favor tente novamente!'
    if (noUpdate) return 'Não há atualizações disponíveis.'
    if (isSuccess) return 'Sua sincronização já está concluída. Você já pode utilizar o app normalmente.'
    return 'Estamos sincronizando seu aplicativo, logo você poderá navegar normalmente.';
  }, [errorOnSync, noUpdate, isSuccess]);

  const syncTitle = useMemo(() => {
    if (errorOnSync) return 'Falha na Sincronização'
    if (isSuccess) return 'Concluído'
    return 'Aguarde...';
  }, [errorOnSync, isSuccess]);

  const syncQtdMessage = useMemo(() => {
    return `${Math.min(isDownloadingFiles.length, downloadingFiles.length)}/${downloadingFiles.length}`;
  }, [isDownloadingFiles, downloadingFiles]);

  const closeModal = () => {
    if (!isOpenedModal) return;
    
    const reload = async () => {
      await unregisterServiceWorker({});
      window.location.reload();
    }
    reload();
  }

  return (
    <Modal id="modal-sync" isOpen={isOpenedModal} isLoading={true} closeModal={closeModal}>
      <S.Container>
        {currentLoading && !isSuccess && (
          <S.BigLoadContent>
            <S.BigLoad>
              <S.CentralLoad />
            </S.BigLoad>
          </S.BigLoadContent>
        )}

        {errorOnSync && <S.Error size={76} />}
        {isSuccess && <S.Success size={76} />}

        <S.LoadingMsg>{syncTitle}</S.LoadingMsg>

        <S.Loader>
          <S.Message>{syncMessage}</S.Message>
        </S.Loader>

        <S.ProgressContent $collapsed={false}>
          <S.ProgressBar $progress={progressPercent}>
            <S.ProgressStyle id="progress-bar" style={{ width: `${progressPercent}%` }} />
            <S.ProgressText>
              <S.ProgressTextLayer $overProgress={false}>
                <S.Span>{fileDownloadProgress}</S.Span>
                <S.Span>{fileDownload}</S.Span>
              </S.ProgressTextLayer>
              <S.ProgressTextLayer $overProgress={true}>
                <S.Span>{fileDownloadProgress}</S.Span>
                <S.Span>{fileDownload}</S.Span>
              </S.ProgressTextLayer>
            </S.ProgressText>
          </S.ProgressBar>
        </S.ProgressContent>

        {currentLoading && !isSuccess && !!downloadSpeedBps && isDownloadingFiles.length > 0 && (
          <>
            <S.Row>
              <S.InternetSpeed>{speedLabel}</S.InternetSpeed>
              <S.FileLoading>Sincronizando {downloadingFileName}</S.FileLoading>
              <S.FilesNumber>{syncQtdMessage}</S.FilesNumber>
            </S.Row>

            <S.LittleLoading>
              <S.LittleLoadingPulse />
            </S.LittleLoading>
          </>
        )}

        {isSuccess && <S.SuccessText>Sincronização concluída!</S.SuccessText>}

        {(isSuccess || errorOnSync) && (
          <S.ButtonClose data-test="modal-sync-close-button" onClick={closeModal}>
            Fechar
          </S.ButtonClose>
        )}
      </S.Container>
    </Modal>
  );
};

export default GlobalModalSync;